import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { getUserById, getUserViolations, suspendUser, banUser } from '@/services/api';
import { Card, CardHeader, CardBody } from '@/components/common/Card';
import Button from '@/components/common/Button';
import { StatusBadge, SeverityBadge } from '@/components/common/Badge';
import Modal, { ModalFooter } from '@/components/common/Modal';
import { PageSpinner } from '@/components/common/Spinner';
import { formatDate, formatDateTime, formatTimeAgo, formatPhoneNumber } from '@/utils/formatters';
import { useAuthStore } from '@/stores/authStore';
import toast from 'react-hot-toast';

export default function UserDetailPage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = useAuthStore();

  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [showBanModal, setShowBanModal] = useState(false);
  const [reason, setReason] = useState('');
  const [duration, setDuration] = useState('7');

  const { data: user, isLoading } = useQuery({
    queryKey: ['user', userId],
    queryFn: () => getUserById(userId!),
    enabled: !!userId,
  });

  const { data: violations } = useQuery({
    queryKey: ['userViolations', userId],
    queryFn: () => getUserViolations(userId!),
    enabled: !!userId,
  });

  const suspendMutation = useMutation({
    mutationFn: () => suspendUser(userId!, reason, parseInt(duration)),
    onSuccess: () => {
      toast.success('User suspended successfully');
      queryClient.invalidateQueries({ queryKey: ['user', userId] });
      setShowSuspendModal(false);
      setReason('');
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to suspend user');
    },
  });

  const banMutation = useMutation({
    mutationFn: () => banUser(userId!, reason),
    onSuccess: () => {
      toast.success('User banned successfully');
      queryClient.invalidateQueries({ queryKey: ['user', userId] });
      setShowBanModal(false);
      setReason('');
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to ban user');
    },
  });

  if (isLoading) {
    return <PageSpinner />;
  }

  if (!user) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">User not found</p>
        <Button onClick={() => navigate('/users')} className="mt-4">
          Back to Users
        </Button>
      </div>
    );
  }

  const accountStatus = user.account_status?.status || 'active';
  const canSuspend = hasPermission('users', 'suspend');
  const canBan = hasPermission('users', 'ban');

  return (
    <div className="space-y-6">
      {/* Back button */}
      <button
        onClick={() => navigate('/users')}
        className="flex items-center gap-2 text-gray-500 hover:text-gray-700"
      >
        <ArrowLeftIcon className="w-4 h-4" />
        Back to Users
      </button>

      {/* User header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center text-2xl font-bold text-gray-600">
            {user.full_name?.[0]?.toUpperCase() || user.email[0].toUpperCase()}
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{user.full_name || 'No Name'}</h1>
            <p className="text-gray-500">{user.email}</p>
            <div className="mt-2">
              <StatusBadge status={accountStatus} />
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          {canSuspend && accountStatus === 'active' && (
            <Button variant="secondary" onClick={() => setShowSuspendModal(true)}>
              Suspend
            </Button>
          )}
          {canBan && accountStatus !== 'banned' && (
            <Button variant="danger" onClick={() => setShowBanModal(true)}>
              Ban
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Info */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <h2 className="text-lg font-semibold">Profile Information</h2>
          </CardHeader>
          <CardBody>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <dt className="text-sm font-medium text-gray-500">Full Name</dt>
                <dd className="mt-1 text-gray-900">{user.full_name || '-'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Email</dt>
                <dd className="mt-1 text-gray-900">{user.email}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Phone</dt>
                <dd className="mt-1 text-gray-900">{formatPhoneNumber(user.phone_number)}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Location</dt>
                <dd className="mt-1 text-gray-900">
                  {user.city || user.state ? `${user.city || ''}${user.city && user.state ? ', ' : ''}${user.state || ''}` : '-'}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Verified</dt>
                <dd className="mt-1 text-gray-900">{user.is_verified ? 'Yes' : 'No'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Role</dt>
                <dd className="mt-1 text-gray-900">{user.role || 'User'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Joined</dt>
                <dd className="mt-1 text-gray-900">{formatDate(user.created_at)}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Last Active</dt>
                <dd className="mt-1 text-gray-900">{user.last_active ? formatTimeAgo(user.last_active) : 'Never'}</dd>
              </div>
            </dl>
          </CardBody>
        </Card>

        {/* Account Status */}
        <Card>
          <CardHeader>
            <h2 className="text-lg font-semibold">Account Status</h2>
          </CardHeader>
          <CardBody>
            <div className="space-y-4">
              <div>
                <dt className="text-sm font-medium text-gray-500">Status</dt>
                <dd className="mt-1">
                  <StatusBadge status={accountStatus} />
                </dd>
              </div>
              {user.account_status?.reason && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Reason</dt>
                  <dd className="mt-1 text-gray-900">{user.account_status.reason}</dd>
                </div>
              )}
              {user.account_status?.suspended_until && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Suspended Until</dt>
                  <dd className="mt-1 text-gray-900">{formatDateTime(user.account_status.suspended_until)}</dd>
                </div>
              )}
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Violations */}
      <Card>
        <CardHeader>
          <h2 className="text-lg font-semibold">Violation History ({violations?.length || 0})</h2>
        </CardHeader>
        <CardBody>
          {violations && violations.length > 0 ? (
            <div className="space-y-4">
              {violations.map((violation) => (
                <div key={violation.id} className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900 capitalize">
                        {violation.violation_type.replace('_', ' ')}
                      </span>
                      <SeverityBadge severity={violation.severity} />
                    </div>
                    {violation.notes && (
                      <p className="mt-1 text-sm text-gray-500">{violation.notes}</p>
                    )}
                    <p className="mt-2 text-xs text-gray-400">
                      Action: {violation.action_taken?.replace('_', ' ') || 'None'}
                    </p>
                  </div>
                  <span className="text-sm text-gray-500">{formatDate(violation.created_at)}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">No violations on record</p>
          )}
        </CardBody>
      </Card>

      {/* Suspend Modal */}
      <Modal
        isOpen={showSuspendModal}
        onClose={() => setShowSuspendModal(false)}
        title="Suspend User"
        description="Temporarily suspend this user's access"
      >
        <div className="space-y-4">
          <div>
            <label htmlFor="suspend-reason" className="block text-sm font-medium text-gray-700 mb-1">
              Reason
            </label>
            <textarea
              id="suspend-reason"
              name="suspend_reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
              rows={3}
              placeholder="Enter the reason for suspension..."
              aria-label="Suspension reason"
            />
          </div>
          <div>
            <label htmlFor="suspend-duration" className="block text-sm font-medium text-gray-700 mb-1">
              Duration (days)
            </label>
            <select
              id="suspend-duration"
              name="suspend_duration"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
              aria-label="Suspension duration in days"
            >
              <option value="1">1 day</option>
              <option value="3">3 days</option>
              <option value="7">7 days</option>
              <option value="14">14 days</option>
              <option value="30">30 days</option>
              <option value="90">90 days</option>
            </select>
          </div>
        </div>
        <ModalFooter>
          <Button variant="secondary" onClick={() => setShowSuspendModal(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => suspendMutation.mutate()}
            loading={suspendMutation.isPending}
            disabled={!reason.trim()}
          >
            Suspend User
          </Button>
        </ModalFooter>
      </Modal>

      {/* Ban Modal */}
      <Modal
        isOpen={showBanModal}
        onClose={() => setShowBanModal(false)}
        title="Ban User"
        description="Permanently ban this user from the platform"
      >
        <div className="space-y-4">
          <div className="p-4 bg-red-50 rounded-lg">
            <p className="text-sm text-red-700">
              <strong>Warning:</strong> This action is permanent. The user will be immediately signed out and will not be able to access their account.
            </p>
          </div>
          <div>
            <label htmlFor="ban-reason" className="block text-sm font-medium text-gray-700 mb-1">
              Reason
            </label>
            <textarea
              id="ban-reason"
              name="ban_reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
              rows={3}
              placeholder="Enter the reason for ban..."
              aria-label="Ban reason"
            />
          </div>
        </div>
        <ModalFooter>
          <Button variant="secondary" onClick={() => setShowBanModal(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => banMutation.mutate()}
            loading={banMutation.isPending}
            disabled={!reason.trim()}
          >
            Ban User Permanently
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
